/** ריכוז התפלגות לפי אפיקים — the PDF's manager × sleeve percentage matrix.
 * Sleeves are grouped under short merged headers (מניות / קונצרני / ממשלתי /
 * חו"ל / נזילות / סחורות) instead of one row of full sleeve names — spelling
 * out all 11 sleeves in a single header row is too wide for a portrait page
 * and forces the numbers into unreadably narrow columns. */
import { Card, EmptyState } from './Card.js';
import { FundLogo } from './FundChip.js';
import { fmtPct } from '../utils.js';
import { useMoney } from '../money.js';
import { AltReportMarker } from '../altReports.js';
import type { RikuzLineRow } from '@agio/core';

interface MatrixCol {
  sleeveId: string;
  column: string;
  /** Short sub-label under the group header; '' for a single-column group
   * (the group label alone is enough). */
  subLabel: string;
}
interface MatrixGroup {
  label: string;
  cols: MatrixCol[];
}

const GROUPS: MatrixGroup[] = [
  { label: 'מניות', cols: [
    { sleeveId: 'StocksIL', column: 'stocks_il', subLabel: 'ישראל' },
    { sleeveId: 'StocksForeign', column: 'stocks_foreign', subLabel: 'חו"ל' },
  ] },
  { label: 'אג"ח קונצרני', cols: [
    { sleeveId: 'BondsNisCorp', column: 'bonds_nis_corp', subLabel: 'שקלי' },
    { sleeveId: 'BondsCpiCorp', column: 'bonds_cpi_corp', subLabel: 'צמוד מדד' },
  ] },
  { label: 'אג"ח ממשלתי', cols: [
    { sleeveId: 'BondsNisGov', column: 'bonds_nis_gov', subLabel: 'שקלי' },
    { sleeveId: 'BondsCpiGov', column: 'bonds_cpi_gov', subLabel: 'צמוד מדד' },
  ] },
  { label: 'אג"ח חו"ל', cols: [{ sleeveId: 'BondsForeign', column: 'bonds_foreign', subLabel: '' }] },
  { label: 'נזילות', cols: [
    { sleeveId: 'MoneyMarket', column: 'money_market', subLabel: 'מ.ק.מ' },
    { sleeveId: 'CashIls', column: 'cash_ils', subLabel: 'עו"ש ופח"ק' },
    { sleeveId: 'CashFx', column: 'cash_fx', subLabel: 'עו"ש מט"ח' },
  ] },
  { label: 'סחורות', cols: [{ sleeveId: 'Commodities', column: 'commodities', subLabel: '' }] },
  { label: 'אלטרנטיבי', cols: [{ sleeveId: 'Alternatives', column: 'alternatives', subLabel: '' }] },
];
const COLS = GROUPS.flatMap((g) => g.cols);

export const managersMatrixHasData = (lines: RikuzLineRow[]) => lines.length > 0;

export function ManagersMatrix({ lines }: { lines: RikuzLineRow[] }) {
  const money = useMoney();
  if (lines.length === 0) return <Card title="ריכוז התפלגות לפי אפיקים"><EmptyState label="אין נתונים" /></Card>;

  const byFund = new Map<string, { fund: string; total: number; bySleeve: Map<string, number> }>();
  const totals = new Map<string, number>();
  let grand = 0;
  for (const l of lines) {
    if (!byFund.has(l.fund_id)) byFund.set(l.fund_id, { fund: l.fund, total: l.total_value ?? 0, bySleeve: new Map() });
    byFund.get(l.fund_id)!.bySleeve.set(l.sleeve, l.value ?? 0);
    totals.set(l.sleeve, (totals.get(l.sleeve) ?? 0) + (l.value ?? 0));
  }
  for (const f of byFund.values()) grand += f.total;

  const pct = (v: number, total: number) => (total ? fmtPct(v / total, 1) : '—');

  return (
    <Card title="ריכוז התפלגות לפי אפיקים">
      <div className="overflow-x-auto">
        <table className="print-dense-table w-full whitespace-nowrap text-xs">
          <thead>
            <tr className="border-b border-border/50 text-right text-muted-foreground">
              <th rowSpan={2} className="py-2 pe-2 align-bottom font-normal">גוף</th>
              <th rowSpan={2} className="py-2 pe-2 align-bottom font-normal">סכום</th>
              <th rowSpan={2} className="py-2 pe-2 align-bottom font-normal">% מסה"כ</th>
              {GROUPS.map((g) => (
                <th key={g.label} colSpan={g.cols.length} className="border-b border-border/50 px-1.5 py-1.5 text-center font-normal">
                  {g.label}
                </th>
              ))}
              <th rowSpan={2} className="px-1.5 py-2 align-bottom font-normal">סה"כ</th>
            </tr>
            <tr className="border-b border-border text-right text-muted-foreground">
              {COLS.map((c) => (
                <th key={c.sleeveId} className="px-1.5 py-1.5 text-center text-[0.85em] font-normal">{c.subLabel}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {[...byFund.entries()].map(([fundId, f]) => {
              const sleeveSum = [...f.bySleeve.values()].reduce((a, v) => a + v, 0);
              return (
                <tr key={f.fund} className="border-b border-border/50">
                  <td className="print-name-cell flex items-center gap-1.5 py-2 pe-2 font-medium">
                    <FundLogo name={f.fund} size={16} />
                    {f.fund}
                    <AltReportMarker fundId={fundId} />
                  </td>
                  <td className="py-2 pe-2 tabular-nums">{money(f.total, fundId)}</td>
                  <td className="py-2 pe-2 tabular-nums">{pct(f.total, grand)}</td>
                  {COLS.map((c) => {
                    const v = f.bySleeve.get(c.column) ?? 0;
                    return (
                      <td key={c.sleeveId} className="px-1.5 py-2 text-center tabular-nums text-muted-foreground">
                        {v ? pct(v, f.total) : '—'}
                      </td>
                    );
                  })}
                  <td className="px-1.5 py-2 text-center font-medium tabular-nums">{pct(sleeveSum, f.total)}</td>
                </tr>
              );
            })}
            <tr className="border-t border-white/20 bg-white/5 font-bold text-success">
              <td className="py-2.5 pe-2">סה"כ נכסים</td>
              <td className="py-2.5 pe-2 tabular-nums">{money(grand, byFund.keys())}</td>
              <td className="py-2.5 pe-2 tabular-nums">{pct(grand, grand)}</td>
              {COLS.map((c) => (
                <td key={c.sleeveId} className="px-1.5 py-2.5 text-center tabular-nums">
                  {pct(totals.get(c.column) ?? 0, grand)}
                </td>
              ))}
              <td className="px-1.5 py-2.5 text-center tabular-nums">
                {pct([...totals.values()].reduce((a, v) => a + v, 0), grand)}
              </td>
            </tr>
          </tbody>
        </table>
      </div>
    </Card>
  );
}
